import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Download, CheckCircle2, XCircle, Search } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';
import api from '../services/api';

const fecha = (d) => (d ? new Date(d).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const COLORES = ['#2563eb', '#16a34a', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
const POR_PAGINA = 25;

function Kpi({ titulo, valor, sub, color = 'text-gray-900 dark:text-white' }) {
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{titulo}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{valor}</p>
            {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>}
        </div>
    );
}

function Caja({ titulo, children, className = '' }) {
    return (
        <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col h-full ${className}`}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700"><h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{titulo}</h3></div>
            <div className="p-5 flex-1 flex flex-col">{children}</div>
        </div>
    );
}

/** Seguridad de los equipos (datos de Vicarius): cobertura de programas, por empresa, sistemas y detalle por equipo. */
export default function ReporteSeguridad() {
    const navigate = useNavigate();
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    const [descargando, setDescargando] = useState(false);
    const [filtro, setFiltro] = useState({ q: '', empresa: '', estado: '' });
    const [pagina, setPagina] = useState(1);

    useEffect(() => {
        api.get('/vicarius/reporte').then((r) => setD(r.data)).catch((e) => setError(e.response?.data?.message || 'No se pudo cargar la seguridad de equipos.'));
    }, []);

    useEffect(() => { setPagina(1); }, [filtro]);

    const descargar = async () => {
        setDescargando(true);
        try {
            const r = await api.get('/vicarius/reporte/excel', { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `Seguridad_equipos_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch { setError('No se pudo descargar el Excel.'); } finally { setDescargando(false); }
    };

    const equipos = useMemo(() => {
        if (!d) return [];
        const q = filtro.q.trim().toLowerCase();
        return d.equipos.filter((e) => {
            if (q && !`${e.codigo} ${e.asignado || ''} ${e.usuario || ''} ${e.ip || ''}`.toLowerCase().includes(q)) return false;
            if (filtro.empresa && e.empresa !== filtro.empresa) return false;
            const faltaAlgo = d.programas.some((n) => e.programas[n]?.requerido && !e.programas[n]?.detectado);
            switch (filtro.estado) {
                case 'sinVicarius': return !e.enVicarius;
                case 'faltaPrograma': return e.enVicarius && faltaAlgo;
                case 'sinReportar': return e.sinReportar;
                case 'soViejo': return e.soDesactualizado;
                case 'ok': return e.enVicarius && !faltaAlgo && !e.sinReportar && !e.soDesactualizado;
                default: return true;
            }
        });
    }, [d, filtro]);

    if (error && !d) return <p className="text-sm text-red-500">{error}</p>;
    if (!d) return null;

    const r = d.resumen;
    const totalPaginas = Math.max(1, Math.ceil(equipos.length / POR_PAGINA));
    const pag = Math.min(pagina, totalPaginas);
    const visibles = equipos.slice((pag - 1) * POR_PAGINA, pag * POR_PAGINA);
    const empresas = [...new Set(d.equipos.map((e) => e.empresa))].sort();
    const dataPrograma = d.porPrograma.map((p) => ({ nombre: p.programa, Instalado: p.instalados, Falta: p.faltan, cobertura: p.cobertura }));
    const dataEmpresa = d.porEmpresa.map((e) => ({ nombre: e.empresa, 'Con Vicarius': e.conVicarius, 'Sin Vicarius': e.equipos - e.conVicarius }));

    return (
        <section className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Seguridad de equipos</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Datos de Vicarius al {fecha(r.ultimaSincronizacion)} · {r.totalEquipos} equipos (notebooks, equipos y desktops)</p>
                    </div>
                </div>
                <button type="button" onClick={descargar} disabled={descargando}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                    <Download size={14} /> {descargando ? 'Generando…' : 'Descargar Excel'}
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <Kpi titulo="Cobertura Vicarius" valor={`${r.coberturaVicarius}%`} sub={`${r.conVicarius} de ${r.totalEquipos}`} color="text-blue-600" />
                <Kpi titulo="Con todos los programas" valor={`${r.coberturaCompleta}%`} sub={`${r.conTodosLosProgramas} equipos completos`} color="text-emerald-600" />
                <Kpi titulo="Sin Vicarius" valor={r.sinVicarius} sub="No aparecen en Vicarius" color="text-red-600" />
                <Kpi titulo="Sin reportar" valor={r.sinReportar} sub={`Más de ${r.diasSinReportar} días`} color="text-amber-600" />
                <Kpi titulo="SO desactualizado" valor={r.soDesactualizado} sub="Windows 10 o anterior" color="text-amber-600" />
                <Kpi titulo="Vulnerabilidades" valor={r.vulnerabilidadesTotal.toLocaleString('es-CL')} sub={`${r.riesgoAlto} equipos con riesgo alto`} color="text-red-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                <Caja titulo="Cobertura por programa de seguridad">
                    <div className="flex-1 relative min-h-[220px]">
                        <div className="absolute inset-0"><ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dataPrograma} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                                <YAxis type="category" dataKey="nombre" tick={{ fontSize: 12 }} width={90} />
                                <Tooltip />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="Instalado" stackId="a" fill="#16a34a" />
                                <Bar dataKey="Falta" stackId="a" fill="#ef4444" />
                            </BarChart>
                        </ResponsiveContainer></div>
                    </div>
                    <table className="w-full text-sm mt-3">
                        <thead><tr className="text-[11px] uppercase text-gray-500 text-left"><th className="py-1">Programa</th><th className="text-right">Instalado</th><th className="text-right">Falta</th><th className="text-right">Cobertura</th></tr></thead>
                        <tbody>
                            {d.porPrograma.map((p) => (
                                <tr key={p.programa} className="border-t border-gray-100 dark:border-gray-700">
                                    <td className="py-1.5 text-gray-800 dark:text-gray-100">{p.programa}{p.requerido ? '' : ' (opcional)'}</td>
                                    <td className="text-right text-emerald-600 font-medium">{p.instalados}</td>
                                    <td className="text-right text-red-600 font-medium">{p.faltan}</td>
                                    <td className="text-right font-semibold">{p.cobertura}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Caja>

                <Caja titulo="Equipos con Vicarius por empresa">
                    <div className="flex-1 relative min-h-[220px]">
                        <div className="absolute inset-0"><ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dataEmpresa}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="nombre" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="Con Vicarius" stackId="a" fill="#2563eb" />
                                <Bar dataKey="Sin Vicarius" stackId="a" fill="#ef4444" />
                            </BarChart>
                        </ResponsiveContainer></div>
                    </div>
                </Caja>

                <Caja titulo="Sistemas operativos">
                    <div className="space-y-3 flex-1 flex flex-col justify-center">
                        {d.sistemasOperativos.map((s, i) => (
                            <div key={s.nombre}>
                                <div className="flex justify-between text-sm"><span className="text-gray-700 dark:text-gray-200">{s.nombre}</span><span className="font-semibold">{s.total}</span></div>
                                <div className="h-1.5 rounded bg-gray-100 dark:bg-gray-700"><div className="h-1.5 rounded" style={{ width: `${(s.total / Math.max(1, r.conVicarius)) * 100}%`, background: COLORES[i % COLORES.length] }} /></div>
                            </div>
                        ))}
                    </div>
                </Caja>

                <Caja titulo="Último reporte de los equipos">
                    <div className="flex-1 relative min-h-[220px]">
                        <div className="absolute inset-0"><ResponsiveContainer width="100%" height="100%">
                            <BarChart data={d.ultimoReporte}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="total" name="Equipos" radius={[3, 3, 0, 0]}>
                                    {d.ultimoReporte.map((_, i) => <Cell key={i} fill={['#16a34a', '#84cc16', '#f59e0b', '#ef4444'][i]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer></div>
                    </div>
                </Caja>
            </div>

            <Caja titulo="Cobertura por empresa y programa">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="text-[11px] uppercase text-gray-500 text-left">
                                <th className="py-2 pr-4">Empresa</th><th className="px-2 text-right">Equipos</th><th className="px-2 text-right">Con Vicarius</th>
                                {d.programas.map((n) => <th key={n} className="px-2 text-right">{n}</th>)}
                                <th className="px-2 text-right">Sin reportar</th><th className="px-2 text-right">SO viejo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {d.porEmpresa.map((e) => (
                                <tr key={e.empresa} className="border-t border-gray-100 dark:border-gray-700">
                                    <td className="py-1.5 pr-4 font-medium text-gray-800 dark:text-gray-100">{e.empresa}</td>
                                    <td className="px-2 text-right">{e.equipos}</td>
                                    <td className="px-2 text-right">{e.conVicarius} <span className="text-gray-400">({e.coberturaVicarius}%)</span></td>
                                    {d.programas.map((n) => <td key={n} className="px-2 text-right">{e.programas[n] ?? 0}</td>)}
                                    <td className={`px-2 text-right ${e.sinReportar ? 'text-amber-600 font-semibold' : ''}`}>{e.sinReportar}</td>
                                    <td className={`px-2 text-right ${e.soDesactualizado ? 'text-amber-600 font-semibold' : ''}`}>{e.soDesactualizado}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Caja>

            <Caja titulo={`Detalle por equipo (${equipos.length})`}>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input value={filtro.q} onChange={(e) => setFiltro((f) => ({ ...f, q: e.target.value }))} placeholder="Buscar equipo, persona o IP…"
                            className="pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white w-64 max-w-full" />
                    </div>
                    <select value={filtro.empresa} onChange={(e) => setFiltro((f) => ({ ...f, empresa: e.target.value }))} className="py-1.5 px-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                        <option value="">Todas las empresas</option>
                        {empresas.map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                    <select value={filtro.estado} onChange={(e) => setFiltro((f) => ({ ...f, estado: e.target.value }))} className="py-1.5 px-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                        <option value="">Todos los estados</option>
                        <option value="sinVicarius">Sin Vicarius</option>
                        <option value="faltaPrograma">Falta algún programa</option>
                        <option value="sinReportar">Sin reportar</option>
                        <option value="soViejo">Sistema desactualizado</option>
                        <option value="ok">Todo en orden</option>
                    </select>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="text-[11px] uppercase text-gray-500 text-left">
                            <tr>
                                <th className="py-2 pr-3">Equipo</th><th className="pr-3">Responsable</th><th className="pr-3">Empresa</th><th className="pr-3">Sistema</th>
                                <th className="pr-3">Último reporte</th>
                                {d.programas.map((n) => <th key={n} className="pr-3 text-center">{n}</th>)}
                                <th className="pr-3 text-right">Vuln.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibles.map((e) => (
                                <tr key={e.activoId} onClick={() => navigate(`/activos/${e.codigo}?tab=software`)} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
                                    <td className="py-1.5 pr-3 font-mono text-xs font-semibold">{e.codigo}</td>
                                    <td className="pr-3 text-gray-700 dark:text-gray-200 whitespace-nowrap">{e.asignado || '—'}</td>
                                    <td className="pr-3 text-gray-500">{e.empresa}</td>
                                    <td className={`pr-3 whitespace-nowrap ${e.soDesactualizado ? 'text-amber-600 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>{e.enVicarius ? (e.sistemaOperativo || '—') : <span className="text-red-500">Sin Vicarius</span>}</td>
                                    <td className={`pr-3 whitespace-nowrap text-xs ${e.sinReportar ? 'text-amber-600 font-semibold' : 'text-gray-500'}`}>{e.enVicarius ? fecha(e.ultimoReporte) : '—'}</td>
                                    {d.programas.map((n) => (
                                        <td key={n} className="pr-3 text-center" title={e.programas[n]?.evidencia || ''}>
                                            {e.programas[n]?.detectado ? <CheckCircle2 size={16} className="inline text-emerald-600" /> : <XCircle size={16} className="inline text-red-400" />}
                                        </td>
                                    ))}
                                    <td className="pr-3 text-right text-gray-600 dark:text-gray-300">{e.vulnerabilidades ?? '—'}</td>
                                </tr>
                            ))}
                            {visibles.length === 0 && <tr><td colSpan={6 + d.programas.length} className="py-6 text-center text-gray-400">Sin equipos con este filtro</td></tr>}
                        </tbody>
                    </table>
                </div>
                {totalPaginas > 1 && (
                    <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                        <span>Mostrando {(pag - 1) * POR_PAGINA + 1}–{Math.min(pag * POR_PAGINA, equipos.length)} de {equipos.length}</span>
                        <div className="flex items-center gap-1">
                            <button type="button" disabled={pag === 1} onClick={() => setPagina(pag - 1)} className="px-2.5 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40">‹</button>
                            <span className="px-2">Página {pag} de {totalPaginas}</span>
                            <button type="button" disabled={pag === totalPaginas} onClick={() => setPagina(pag + 1)} className="px-2.5 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40">›</button>
                        </div>
                    </div>
                )}
            </Caja>
        </section>
    );
}
